// React and MobX
import React from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, Card, CardActionArea, CardContent, Typography } from "@material-ui/core";
import ArrowForwardIosOutlinedIcon from "@material-ui/icons/ArrowForwardIosOutlined";

// Core
import { useRouter } from "Core/Base";

// Custom Styles
import { SignatureSectionStyles } from "Custom/StylesAppSpecific/DashboardSubViews/SignaturePageStyling";

// Custom ViewModels
import { SignaturesSectionViewModel } from "Custom/ViewModels/DashboardSubViewModels/SignaturesSectionViewModel";
import { SignatureRequestStatusType } from "Custom/Models/Enums/SignatureStatusType";

interface IProps {
    viewModel: SignaturesSectionViewModel;
    sectionType?: string;
}

export const ReservationCards: React.FC<IProps> = props => {
    const router = useRouter();
    const signatureSectionStyles = SignatureSectionStyles();

    const handleReservationCardClick = (reservationId: string) => {
        // Navigate to reservation form with signature functionality
        router.history.push(`/reservation/${props.viewModel.model.reservation.propertyId}/sign/${reservationId}`);
    };

    const renderReservationCard = (viewModel: SignaturesSectionViewModel, sectionType?: string) => {
        const signatureStatus = viewModel.model.signatureStatus;
        const isExpired = signatureStatus && new Date(signatureStatus.expiryDate) < new Date();
        const isCompleted = signatureStatus && signatureStatus.requestStatus === SignatureRequestStatusType.Completed;

        let statusText = "";
        let expiryText = "";

        if (signatureStatus) {
            // Calculate signature progress manually
            // SignatureStatusType: Pending = 0, Complete = 1, Expired = 2
            // Only count purchasers who have tokens (meaning they need to sign)
            const purchaserOneRequired = signatureStatus.purchaserOneToken && signatureStatus.purchaserOneToken !== "";
            const purchaserTwoRequired = signatureStatus.purchaserTwoToken && signatureStatus.purchaserTwoToken !== "";

            const completed =
                (signatureStatus.purchaserOneStatus === 1 && purchaserOneRequired ? 1 : 0) + (signatureStatus.purchaserTwoStatus === 1 && purchaserTwoRequired ? 1 : 0);
            const total = (purchaserOneRequired ? 1 : 0) + (purchaserTwoRequired ? 1 : 0);
            statusText = `${completed}/${total} signatures required`;

            if (isCompleted) {
                statusText = "All required signatures complete";
                if (signatureStatus.completedDate && signatureStatus.completedDate !== "") {
                    const completedDate = new Date(signatureStatus.completedDate);
                    if (!isNaN(completedDate.getTime())) {
                        expiryText = `Completed at ${completedDate.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                        })} on the ${completedDate.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                        })}`;
                    } else {
                        expiryText = "Completion date not available";
                    }
                } else {
                    expiryText = "Completion date not available";
                }
            } else if (signatureStatus.expiryDate && signatureStatus.expiryDate !== "") {
                const expiryDate = new Date(signatureStatus.expiryDate);

                if (!isNaN(expiryDate.getTime())) {
                    expiryText = `Link expires at ${expiryDate.toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                    })} on the ${expiryDate.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                    })}`;
                } else {
                    expiryText = "Link expiry date not available";
                }
            } else {
                expiryText = "Link expiry date not available";
            }
        }

        const cardContent = (
            <CardContent>
                <Box className={signatureSectionStyles.cardHead}>
                    <Box>
                        <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: "4px" }}>
                            Reservation form
                        </Typography>
                        {signatureStatus && (
                            <Box>
                                <Typography variant="body2" style={{ marginBottom: "2px" }}>
                                    {statusText}
                                </Typography>
                                <Typography variant="body2">{expiryText}</Typography>
                            </Box>
                        )}
                    </Box>
                    {sectionType !== "Completed" && (
                        <Box className={signatureSectionStyles.navigateToArrow}>
                            <ArrowForwardIosOutlinedIcon />
                        </Box>
                    )}
                </Box>
            </CardContent>
        );

        const interactiveContent =
            sectionType === "Completed" ? cardContent : <CardActionArea onClick={() => handleReservationCardClick(viewModel.model.reservation.id!)}>{cardContent}</CardActionArea>;

        return (
            <Box key={viewModel.model.reservation.id} className={signatureSectionStyles.signatureRow}>
                <Card className={signatureSectionStyles.signatureCard}>{interactiveContent}</Card>
            </Box>
        );
    };

    return useObserver(() => {
        const viewModel = props.viewModel;
        const signatureStatus = viewModel?.model?.signatureStatus;

        let shouldShowCard = false;
        const isExpired = signatureStatus && new Date(signatureStatus.expiryDate) < new Date();

        if (props.sectionType === "Active") {
            // Active section: Show cards where the current user has NOT signed yet
            shouldShowCard = signatureStatus && signatureStatus.requestStatus === SignatureRequestStatusType.Published && !isExpired && !viewModel.isSignatureComplete;
        } else if (props.sectionType === "Pending") {
            // Pending section: Show cards where the current user HAS signed
            shouldShowCard = signatureStatus && signatureStatus.requestStatus === SignatureRequestStatusType.Published && !isExpired && viewModel.isSignatureComplete;
        } else if (props.sectionType === "Completed") {
            // Completed section: Show cards where all signatures are complete
            shouldShowCard = signatureStatus && signatureStatus.requestStatus === SignatureRequestStatusType.Completed;
        } else {
            // If no sectionType specified, show all
            shouldShowCard = !!signatureStatus;
        }

        const fallbackCopy =
            props.sectionType === "Completed" ? "No completed signature requests yet" : `No ${props.sectionType?.toLowerCase() || ""} signature requests available`;

        return <Box>{shouldShowCard ? renderReservationCard(viewModel, props.sectionType) : <Typography>{fallbackCopy}</Typography>}</Box>;
    });
};
